import { CameraControls } from '@react-three/drei';
import * as holdEvent from 'hold-event';
import { useEffect } from "react";
import * as THREE from 'three';

type HoldListener = Parameters<holdEvent.KeyboardKeyHold['addEventListener']>[1];
type DispatcherEvent = Parameters<HoldListener>[0];

interface KeyboardControlsComponentProps {
    cameraControls: CameraControls;
    speedFactor: number;
}

const KeyboardControlsComponent = ({ cameraControls, speedFactor }: KeyboardControlsComponentProps) => {

    const holdIntervalDelay = 16.666;

    const wKey = new holdEvent.KeyboardKeyHold( 'KeyW', holdIntervalDelay );
    const sKey = new holdEvent.KeyboardKeyHold( 'KeyS', holdIntervalDelay );
    const aKey = new holdEvent.KeyboardKeyHold( 'KeyA', holdIntervalDelay );
    const dKey = new holdEvent.KeyboardKeyHold( 'KeyD', holdIntervalDelay );
    const qKey = new holdEvent.KeyboardKeyHold( 'KeyQ', holdIntervalDelay );
    const eKey = new holdEvent.KeyboardKeyHold( 'KeyE', holdIntervalDelay );

    const arrowUpKey = new holdEvent.KeyboardKeyHold( 'ArrowUp', holdIntervalDelay );
    const arrowDownKey = new holdEvent.KeyboardKeyHold( 'ArrowDown', holdIntervalDelay );
    const arrowLeftKey = new holdEvent.KeyboardKeyHold( 'ArrowLeft', holdIntervalDelay );
    const arrowRightKey = new holdEvent.KeyboardKeyHold( 'ArrowRight', holdIntervalDelay );

    const shiftLeftKey = new holdEvent.KeyboardKeyHold( 'ShiftLeft', holdIntervalDelay );
    const shiftRightKey = new holdEvent.KeyboardKeyHold( 'ShiftRight', holdIntervalDelay );

    const forwardEvent = (event: DispatcherEvent | undefined) => cameraControls.forward(   speedFactor * event?.deltaTime, true );
    const backwardEvent = (event: DispatcherEvent | undefined) => cameraControls.forward (   -speedFactor * event?.deltaTime, true );
    const leftEvent = (event: DispatcherEvent | undefined) => cameraControls.truck( -speedFactor * event?.deltaTime, 0, true );
    const rightEvent = (event: DispatcherEvent | undefined) => cameraControls.truck(   speedFactor * event?.deltaTime, 0, true );
    const moveUpEvent = (event: DispatcherEvent | undefined) => cameraControls.truck( 0, -speedFactor * event?.deltaTime, true );
    const moveDownEvent = (event: DispatcherEvent | undefined) => cameraControls.truck( 0,  speedFactor * event?.deltaTime, true );

    const rotateTargetAroundCamera = (angle: number) => {
        const cameraPosition = new THREE.Vector3();
        const targetPosition = new THREE.Vector3();
        cameraControls.getPosition(cameraPosition);
        cameraControls.getTarget(targetPosition);

        // Get direction from camera to target
        const direction = targetPosition.clone().sub(cameraPosition);
        
        // Rotate direction around Z axis
        direction.applyAxisAngle(new THREE.Vector3(0, 0, 1), angle);
        
        // Calculate new target position
        const newTarget = cameraPosition.clone().add(direction);
        
        cameraControls.setTarget(newTarget.x, newTarget.y, newTarget.z, true);
    };

    const turnLeftEvent = (event: DispatcherEvent | undefined) => rotateTargetAroundCamera(speedFactor * event?.deltaTime * 0.5);
    const turnRightEvent = (event: DispatcherEvent | undefined) => rotateTargetAroundCamera(-speedFactor * event?.deltaTime * 0.5);

    const isShiftHeld = () => shiftLeftKey.holding || shiftRightKey.holding;

    const arrowLeftEvent = (event: DispatcherEvent | undefined) => isShiftHeld() ? leftEvent(event) : turnLeftEvent(event);
    const arrowRightEvent = (event: DispatcherEvent | undefined) => isShiftHeld() ? rightEvent(event) : turnRightEvent(event);
    const arrowUpEvent = (event: DispatcherEvent | undefined) => isShiftHeld() ? moveUpEvent(event) : forwardEvent(event);
    const arrowDownEvent = (event: DispatcherEvent | undefined) => isShiftHeld() ? moveDownEvent(event) : backwardEvent(event);

    useEffect(() => {
        wKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            forwardEvent
        );

        sKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            backwardEvent
        );

        aKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            leftEvent
        );

        dKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            rightEvent
        );

        qKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            turnLeftEvent
        );

        eKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            turnRightEvent
        );

        arrowUpKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            arrowUpEvent
        );

        arrowDownKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            arrowDownEvent
        );

        arrowLeftKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            arrowLeftEvent
        );

        arrowRightKey.addEventListener(
            holdEvent.HOLD_EVENT_TYPE.HOLDING,
            arrowRightEvent
        );

        return () => {
            wKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                forwardEvent
            );

            sKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                backwardEvent
            );

            aKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                leftEvent
            );

            dKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                rightEvent
            );

            qKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                turnLeftEvent
            );

            eKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                turnRightEvent
            );

            arrowUpKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                arrowUpEvent
            );

            arrowDownKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                arrowDownEvent
            );

            arrowLeftKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                arrowLeftEvent
            );

            arrowRightKey.removeEventListener(
                holdEvent.HOLD_EVENT_TYPE.HOLDING,
                arrowRightEvent
            );
        };
    }, [cameraControls])

    return (<></>);
};

export default KeyboardControlsComponent;